import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Spin } from 'antd';
import {
  freePhotoViewModeAtom,
  freePhotoFiltersAtom,
  freePhotoPageAtom,
  freePhotoPageSizeAtom,
  selectedFreePhotoAtom,
} from '~/stores/FreePhotoStore';
import { useFreePhotoList } from './hooks/useFreePhotoList';
import { useFreePhotoDownload } from './hooks/useFreePhotoDownload';
import { useProjects } from './hooks/useProjects';
import { FreePhotoHeader } from './components/FreePhotoHeader';
import { FreePhotoFiltersComponent } from './components/FreePhotoFilters';
import { FreePhotoGrid } from './components/FreePhotoGrid';
import { FreePhotoList } from './components/FreePhotoList';
import { FreePhotoDetailModal } from './components/FreePhotoDetailModal';
import { FreePhotoMapView } from './components/FreePhotoMapView';
import type { FreePhoto_Base } from '~/common/typescript/models/FreePhoto';

/**
 * Main FreePhoto screen component
 */
const FreePhotoScreen: React.FC = () => {
  const [viewMode, setViewMode] = useAtom(freePhotoViewModeAtom);
  const [filters, setFilters] = useAtom(freePhotoFiltersAtom);
  const [currentPage, setCurrentPage] = useAtom(freePhotoPageAtom);
  const pageSize = useAtom(freePhotoPageSizeAtom)[0];
  const [selectedPhoto, setSelectedPhoto] = useAtom(selectedFreePhotoAtom);

  const { photos, loading, error, totalCount } = useFreePhotoList();
  const { downloadPhoto } = useFreePhotoDownload();
  const { projects, loading: projectsLoading } = useProjects();

  const [detailModalVisible, setDetailModalVisible] = useState(false);

  const handleViewModeChange = (mode: 'grid' | 'list' | 'map') => {
    setViewMode(mode);
  };

  const handleFiltersChange = (newFilters: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      sortBy: 'date_desc',
      projectId: undefined,
      dateRange: undefined,
      locationQuery: undefined,
    });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
  };

  const handlePhotoClick = (photo: FreePhoto_Base) => {
    setSelectedPhoto(photo);
    setDetailModalVisible(true);
  };

  const handlePhotoDownload = (photo: FreePhoto_Base) => {
    downloadPhoto(photo.uuid, photo.file_name);
  };

  const handleDetailModalClose = () => {
    setDetailModalVisible(false);
    setSelectedPhoto(null);
  };

  const handleNavigatePhoto = useCallback((direction: 'prev' | 'next') => {
    setSelectedPhoto((current) => {
      if (!current || photos.length === 0) return current;
      const currentIndex = photos.findIndex((p) => p.uuid === current.uuid);
      if (currentIndex === -1) return current;
      let newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0) newIndex = photos.length - 1;
      if (newIndex >= photos.length) newIndex = 0;
      return photos[newIndex];
    });
  }, [photos, setSelectedPhoto]);

  // Keyboard ArrowLeft / ArrowRight navigation when modal is open
  useEffect(() => {
    if (!detailModalVisible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handleNavigatePhoto('prev');
      if (e.key === 'ArrowRight') handleNavigatePhoto('next');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [detailModalVisible, handleNavigatePhoto]);

  if (error) {
    return (
      <UI_ErrorWrapper>
        <p>Không thể tải danh sách ảnh. Vui lòng thử lại sau.</p>
        <p style={{ color: '#999', fontSize: '12px' }}>{error.message}</p>
      </UI_ErrorWrapper>
    );
  }

  return (
    <UI_Wrapper>
      <FreePhotoHeader viewMode={viewMode} totalCount={totalCount} onViewModeChange={handleViewModeChange} />

      <FreePhotoFiltersComponent 
        filters={filters} 
        onFiltersChange={handleFiltersChange} 
        onClearFilters={handleClearFilters}
        projects={projects}
        projectsLoading={projectsLoading}
      />

      <UI_Content $viewMode={viewMode}>
        {loading && photos.length === 0 ? (
          <UI_LoadingWrapper>
            <Spin size="large" tip="Đang tải ảnh..." />
          </UI_LoadingWrapper>
        ) : (
          <>
            {viewMode === 'grid' && (
              <FreePhotoGrid
                photos={photos}
                loading={loading}
                currentPage={currentPage}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                onPhotoClick={handlePhotoClick}
              />
            )}

            {viewMode === 'list' && (
              <FreePhotoList
                photos={photos}
                loading={loading}
                currentPage={currentPage}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                onPhotoView={handlePhotoClick}
                onPhotoDownload={handlePhotoDownload}
              />
            )}

            {viewMode === 'map' && <FreePhotoMapView photos={photos} onPhotoClick={handlePhotoClick} />}
          </>
        )}
      </UI_Content>

      <FreePhotoDetailModal visible={detailModalVisible} onClose={handleDetailModalClose} onNavigate={handleNavigatePhoto} />
    </UI_Wrapper>
  );
};

export default FreePhotoScreen;

const UI_Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #fff;
`;

const UI_Content = styled.div<{ $viewMode?: string }>`
  flex: 1;
  overflow-y: ${props => props.$viewMode === 'map' ? 'hidden' : 'auto'};
`;

const UI_LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const UI_ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 24px;
  text-align: center;
`;

const UI_MapPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  padding: 24px;
  text-align: center;

  p:first-child {
    font-size: 48px;
    margin-bottom: 16px;
  }
`;